/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {Filters} from '../types/types';
import {
  FilterChecklistOptionBase,
  FilterChecklistValueBase,
  FilterTreeNodeBase
} from './types';

interface OptionType {
  id: number;
  name: string;
}

interface SelectedOptionsType {
  id: string;
  selected: OptionType
  selections: OptionType[]
}

export const getFilterChecklistValueKey = (value: FilterChecklistValueBase) => value?.toString();

const numericLabelPattern = /^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)/;

const compareFilterChecklistLabels = (firstLabel: string, secondLabel: string) => {
  const firstIsNumeric  = numericLabelPattern.test(firstLabel);
  const secondIsNumeric = numericLabelPattern.test(secondLabel);
  let result;
  if (firstIsNumeric !== secondIsNumeric) {
    result = firstIsNumeric ? -1 : 1;
  } else {
    result = firstLabel?.localeCompare(secondLabel, undefined, {
      numeric     : true,
      sensitivity : 'base'
    });
  }
  return result;
};

/**
 * Sorts checklist options with numeric labels before alphabetical labels.
 *
 * ```typescript
 * import {getFilterChecklistSortedOptions} from './utils';
 *
 * ...
 *
 * const sortedOptions = getFilterChecklistSortedOptions(options, getLabel);
 * ```
 */
export function getFilterChecklistSortedOptions<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
>(
  options: T[],
  getOptionLabel: (option: T) => string
) {
  return [...options].sort((firstOption, secondOption) => compareFilterChecklistLabels(
    getOptionLabel(firstOption),
    getOptionLabel(secondOption)
  ));
}

/**
 * Moves selected checklist options ahead of the unselected ones - each group
 * kept in the options' default (alphabetical) order, rather than the order
 * the options were selected in.
 *
 * ```typescript
 * import {getFilterChecklistSelectedFirstOptions} from './utils';
 *
 * ...
 *
 * const orderedOptions = getFilterChecklistSelectedFirstOptions(
 *   options,
 *   selectedValues,
 *   getValue
 * );
 * ```
 */
export function getFilterChecklistSelectedFirstOptions<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
>(
  options: T[],
  selectedValues: FilterChecklistValueBase[],
  getOptionValue: (option: T) => FilterChecklistValueBase
) {
  const selectedKeys = new Set(selectedValues.map(getFilterChecklistValueKey));
  const isSelected   = (option: T) => selectedKeys.has(getFilterChecklistValueKey(getOptionValue(option)));
  return options.map((option, defaultIndex) => ({defaultIndex, option, selected: isSelected(option)}))
    .sort((first, second) => (
      first.selected === second.selected
        ? first.defaultIndex - second.defaultIndex
        : (first.selected ? -1 : 1)
    ))
    .map(({option}) => option);
}

/**
 * Creates a lookup for selected checklist values.
 *
 * ```typescript
 * import {getFilterChecklistSelectedLookup} from './utils';
 *
 * const selectedLookup = getFilterChecklistSelectedLookup(selectedValues);
 * ...
 * ```
 */
export const getFilterChecklistSelectedLookup = (selectedValues: FilterChecklistValueBase[]) => (
  selectedValues.reduce((lookup, value) => ({
    ...lookup,
    [getFilterChecklistValueKey(value)] : true
  }), {} as Record<string, boolean>)
);

/**
 * Filters checklist options by the current search value.
 *
 * ```typescript
 * import {getFilterChecklistVisibleOptions} from './utils';
 *
 * const visibleOptions = getFilterChecklistVisibleOptions(options, searchValue, getLabel);
 * ...
 * ```
 */
export function getFilterChecklistVisibleOptions<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
>(
  options: T[],
  searchValue: string,
  getOptionLabel: (option: T) => string
) {
  const normalizedSearch = searchValue.trim().toLowerCase();
  return normalizedSearch
    ? options.filter(option => getOptionLabel(option).toLowerCase().includes(normalizedSearch))
    : options;
}

/**
 * Resolves selected checklist options from selected values.
 *
 * ```typescript
 * import {getFilterChecklistSelectedOptions} from './utils';
 *
 * const selectedOptions = getFilterChecklistSelectedOptions(options, values, getValue);
 * ...
 * ```
 */
export function getFilterChecklistSelectedOptions<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
>(
  options: T[],
  selectedValues: FilterChecklistValueBase[],
  getOptionValue: (option: T) => FilterChecklistValueBase
) {
  const selectedLookup = getFilterChecklistSelectedLookup(selectedValues);
  return options.filter(item => selectedLookup[getFilterChecklistValueKey(getOptionValue(item))]);
}

export const getFilterTreeNodeKey = (node: FilterTreeNodeBase) => `${node.type ?? 'default'}:${node.id}`;

/**
 * Collects the key for a tree node and every loaded descendant.
 *
 * ```typescript
 * import {collectFilterTreeNodeKeys} from './utils';
 *
 * const selectedKeys = collectFilterTreeNodeKeys(node);
 * ...
 * ```
 */
// a node's own and descendant keys, cached per node object - tree nodes are
// never changed in place (an update replaces the changed node and its
// ancestors), so an unchanged node's keys stay valid. Every row reads these
// on every render, which in a tree of thousands of groups is the hot path ...
const filterTreeNodeKeysCache = new WeakMap<FilterTreeNodeBase, string[]>();

export const collectFilterTreeNodeKeys = (node: FilterTreeNodeBase): string[] => {
  let keys = filterTreeNodeKeysCache.get(node);
  if (!keys) {
    keys = [
      getFilterTreeNodeKey(node),
      ...(node.children ?? []).flatMap(collectFilterTreeNodeKeys)
    ];
    filterTreeNodeKeysCache.set(node, keys);
  }
  return keys;
};

/**
 * Collects keys for every loaded node in a tree checklist.
 *
 * ```typescript
 * import {collectFilterTreeKeys} from './utils';
 *
 * const selectedKeys = collectFilterTreeKeys(nodes);
 * ...
 * ```
 */
export const collectFilterTreeKeys = (nodes: FilterTreeNodeBase[]): string[] => (
  nodes.flatMap(collectFilterTreeNodeKeys)
);

/**
 * Collects keys for tree nodes that contain visible child nodes.
 *
 * ```typescript
 * import {collectFilterTreeBranchKeys} from './utils';
 *
 * ...
 *
 * const expandedKeys = collectFilterTreeBranchKeys(nodes);
 * ```
 */
export const collectFilterTreeBranchKeys = (nodes: FilterTreeNodeBase[]): string[] => (
  nodes.flatMap(node => node.children?.length ? [
    getFilterTreeNodeKey(node),
    ...collectFilterTreeBranchKeys(node.children)
  ] : [])
);

/**
 * Finds the path from a tree root to the requested node, including both nodes.
 *
 * @param nodes     Defines the tree nodes to search.
 * @param targetKey Defines the node key that terminates the path.
 * @returns The matching node path, or an empty array when no node matches.
 */
export const findFilterTreeNodePath = (
  nodes: FilterTreeNodeBase[],
  targetKey: string
): FilterTreeNodeBase[] => {
  for (const node of nodes) {
    if (getFilterTreeNodeKey(node) === targetKey) {
      return [node];
    }
    const childPath = findFilterTreeNodePath(node.children ?? [], targetKey);
    if (childPath.length) {
      return [node, ...childPath];
    }
  }
  return [];
};

/**
 * Filters tree checklist nodes by label while preserving matching ancestors.
 *
 * ```typescript
 * import {filterTreeChecklistNodes} from './utils';
 *
 * const visibleNodes = filterTreeChecklistNodes(nodes, searchTerm);
 * ...
 * ```
 */
export const filterTreeChecklistNodes = (
  nodes: FilterTreeNodeBase[],
  searchTerm: string
): FilterTreeNodeBase[] => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) {
    return nodes;
  }
  return nodes.reduce((result, node) => {
    const children = filterTreeChecklistNodes(node.children ?? [], searchTerm);
    const matches = node.label?.toLowerCase().includes(normalizedSearchTerm);
    if (matches || children.length) {
      result.push({
        ...node,
        ...(node.children || children.length ? {children} : {})
      });
    }
    return result;
  }, [] as FilterTreeNodeBase[]);
};

/**
 * Finds matching tree checklist nodes as a flat list.
 *
 * @param nodes      Defines the tree nodes to search.
 * @param searchTerm Defines the label text to match.
 * @returns Matching nodes in tree order without ancestor wrappers.
 */
export const findFilterTreeChecklistMatches = (
  nodes: FilterTreeNodeBase[],
  searchTerm: string
): FilterTreeNodeBase[] => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) {
    return nodes;
  }
  return nodes.flatMap(node => [
    ...(node.label?.toLowerCase().includes(normalizedSearchTerm) ? [node] : []),
    ...findFilterTreeChecklistMatches(node.children ?? [], searchTerm)
  ]);
};

/**
 * Finds selected tree nodes from a selected key set.
 *
 * ```typescript
 * import {findSelectedFilterTreeNodes} from './utils';
 *
 * const selectedNodes = findSelectedFilterTreeNodes(nodes, selected);
 * ...
 * ```
 */
export const findSelectedFilterTreeNodes = (
  nodes: FilterTreeNodeBase[],
  selected: Set<string>
): FilterTreeNodeBase[] => (
  nodes.flatMap(node => [
    ...(selected.has(getFilterTreeNodeKey(node)) ? [node] : []),
    ...findSelectedFilterTreeNodes(node.children ?? [], selected)
  ])
);

/**
 * Checks whether a tree node has any selected descendant.
 *
 * ```typescript
 * import {hasSelectedFilterTreeDescendant} from './utils';
 *
 * const indeterminate = hasSelectedFilterTreeDescendant(node, selected);
 * ...
 * ```
 */
export const hasSelectedFilterTreeDescendant = (
  node: FilterTreeNodeBase,
  selected: Set<string>
) => (
  (node.children ?? []).some(child => (
    selected.has(getFilterTreeNodeKey(child)) || hasSelectedFilterTreeDescendant(child, selected)
  ))
);

/**
 * Resolves the checked and indeterminate state for a tree checklist node.
 *
 * @param node     Defines the node whose selection state is resolved.
 * @param selected Defines every selected tree node key.
 * @returns The checkbox state for the requested node.
 */
export const getFilterTreeNodeSelectionState = (
  node: FilterTreeNodeBase,
  selected: Set<string>
) => {
  // one pass over the node's (cached) descendant keys answers both whether
  // every descendant is selected and whether any is ...
  const nodeKeys            = collectFilterTreeNodeKeys(node);
  const nodeSelected        = selected.has(nodeKeys[0]);
  let descendantsSelected   = true;
  let hasSelectedDescendant = false;
  for (let index = 1; index < nodeKeys.length; index++) {
    const descendantSelected = selected.has(nodeKeys[index]);
    descendantsSelected   = descendantsSelected && descendantSelected;
    hasSelectedDescendant = hasSelectedDescendant || descendantSelected;
  }
  const checked = nodeSelected && descendantsSelected;
  return {
    checked,
    indeterminate : !checked && (nodeSelected || hasSelectedDescendant)
  };
};

/**
 * Finds fully checked tree nodes while excluding indeterminate ancestors.
 *
 * @param nodes    Defines the tree nodes to inspect.
 * @param selected Defines every selected or retained tree node key.
 * @returns The fully checked tree nodes in tree order.
 */
export const findCheckedFilterTreeNodes = (
  nodes: FilterTreeNodeBase[],
  selected: Set<string>
): FilterTreeNodeBase[] => (
  nodes.flatMap(node => [
    ...(getFilterTreeNodeSelectionState(node, selected).checked ? [node] : []),
    ...findCheckedFilterTreeNodes(node.children ?? [], selected)
  ])
);

// applies an update to the node with this key - only that node and its
// ancestors are replaced, every other node (and the array itself, when the
// key isn't found) is kept as is, so unchanged rows keep their identity and
// skip re-rendering ...
const updateFilterTreeNode = (
  nodes: FilterTreeNodeBase[],
  targetKey: string,
  update: (node: FilterTreeNodeBase) => FilterTreeNodeBase
): FilterTreeNodeBase[] => {
  let changed = false;
  const nextNodes = nodes.map(node => {
    let nextNode = node;
    if (getFilterTreeNodeKey(node) === targetKey) {
      nextNode = update(node);
    } else if (node.children?.length) {
      const nextChildren = updateFilterTreeNode(node.children, targetKey, update);
      if (nextChildren !== node.children) {
        nextNode = {...node, children: nextChildren};
      }
    }
    changed = changed || nextNode !== node;
    return nextNode;
  });
  return changed ? nextNodes : nodes;
};

/**
 * Replaces a node's children after lazy loading tree checklist descendants.
 *
 * ```typescript
 * import {updateFilterTreeNodeChildren} from './utils';
 *
 * const updatedNodes = updateFilterTreeNodeChildren(nodes, nodeKey, children);
 * ...
 * ```
 */
export const updateFilterTreeNodeChildren = (
  nodes: FilterTreeNodeBase[],
  targetKey: string,
  result: FilterTreeNodeBase[] | {
    children: FilterTreeNodeBase[];
    hasMore?: boolean;
  }
): FilterTreeNodeBase[] => {
  const children = Array.isArray(result) ? result : result.children;
  const hasMore  = Array.isArray(result) ? false : !!result.hasMore;
  return updateFilterTreeNode(nodes, targetKey, node => ({
    ...node,
    children,
    childrenLoaded : true,
    hasMore,
    loading        : false
  }));
};

export const updateFilterTreeNodeLoading = (
  nodes: FilterTreeNodeBase[],
  targetKey: string,
  loading: boolean
): FilterTreeNodeBase[] => (
  updateFilterTreeNode(nodes, targetKey, node => ({...node, loading}))
);

const isInstanceofSelectedOptionsType = (obj: any): obj is SelectedOptionsType[] =>
  obj?.length && typeof obj[0] !== 'string';

/**
 * utility function to remove duplicate selections for selected chips display.
 * ```typescript
 *  import {getUniqueSelectedOptions} from './utils';
 *  const options = getUniqueSelectedOptions(selectedOptions);
 * ```
 */
export const getUniqueSelectedOptions = (options: SelectedOptionsType[] | string[]) => {
  if (options?.length) {
    if (isInstanceofSelectedOptionsType(options)) {
      let rawSelections = options[0]?.selections.map(({id}: OptionType) => id);
      return options.filter((item: SelectedOptionsType) => {
        const {selected: {id, name}} = item;
        if (rawSelections.includes(id)) {
          rawSelections = rawSelections.filter(value => value !== id);
          return item;
        }
      }).filter((item) => !isNil(item));
    } else {
      return Array.from(new Set(options));
    }
  }
  return [];
};

/**
 * utility function to provide chips should be displayed.
 * ```typescript
 * import {getFilterSelection} from './utils';
 *
 * const {filterSelected, selectedCount} = getFilterSelection(selectedOptions);
 * ...
 * ```
 */
export const getFilterSelection = (selectedOptions) => {
  const getSelected = (filter: Filters) => (
    selectedOptions?.filter(({id}: any) => id === filter)
  );
  const groupsSelectedFilters = getSelected(Filters.groups);
  const assetsSelectedFilters = getSelected(Filters.assets);
  const devicesSelectedFilters = getSelected(Filters.devices);
  // remove assets/ devices under group has been checked
  const reducedAssets = getReducedData(assetsSelectedFilters, groupsSelectedFilters);
  const reducedDevices = getReducedData(devicesSelectedFilters, groupsSelectedFilters);

  const allSelectedFilters = selectedOptions?.filter(({id}: any) => (
    id !== Filters.groups &&
    id !== Filters.assets &&
    id !== Filters.devices
  ));
  const uniqueSelectedOptions = getUniqueSelectedOptions(groupsSelectedFilters) as any;
  const filterSelected = [...uniqueSelectedOptions, ...reducedAssets, ...reducedDevices, ...allSelectedFilters];
  const selectedCount = filterSelected.length;

  return {filterSelected, selectedCount};
};

/**
 * @private
 * getReducedData filters all child assets/ devices which their parent group is already fully checked
 */
const getReducedData = (data, selectedGroups) => {
  const map = {};
  selectedGroups.forEach(group => {
    const id = group?.selected?.id;
    if (id) {
      map[group.selected.id] = true;
    }
  });
  return data.filter(({selected: {accountId}}) => !map[accountId]);
};

/**
 * Determines whether a group shows all of its children - past its first
 * batch, every loaded child visible and none left to load - which is when its
 * toggle offers to show less rather than all.
 *
 * ```typescript
 * import {isFilterTreeGroupFullyShown} from '@ion/dls/src/filters/utils';
 *
 * ...
 *
 * const showsLess = isFilterTreeGroupFullyShown(node, visibleCounts[key], batchSize);
 * ```
 *
 * @param node         The group.
 * @param visibleCount How many of its children show, when raised.
 * @param batchSize    How many show by default.
 * @returns            `true` when every child shows, otherwise `false`.
 */
export const isFilterTreeGroupFullyShown = (
  node: FilterTreeNodeBase,
  visibleCount: number | undefined,
  batchSize: number
): boolean => {
  const count = visibleCount ?? batchSize;
  return count > batchSize && count >= (node.children?.length ?? 0) && !node.hasMore;
};
